import { SUPPORT_EMAIL } from '@psyomics/ui-common';
import { InputHTMLAttributes, useId } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { Text } from '../../../atoms/text/text';
import { createSubmitHandler } from '../handle-submit';

import { BaseCaptureFormProps, FormLayout, useCollectedErrors } from './verification.shared';

const InlineLinkButton = ({
  children,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
}) => (
  <button className="text-blue-600 underline cursor-pointer" onClick={onClick} type="button">
    {children}
  </button>
);

const getSecondLink = ({
  onChangeNumber,
  onStartAgain,
  showSupportContact,
  t,
}: {
  onChangeNumber?: () => void;
  onStartAgain?: () => void;
  showSupportContact: boolean;
  t: (key: string) => string;
}) => {
  if (onChangeNumber)
    return <InlineLinkButton onClick={onChangeNumber}>{t('page.mfaSetup.code.changeNumber')}</InlineLinkButton>;
  if (onStartAgain)
    return <InlineLinkButton onClick={onStartAgain}>{t('page.mfaSetup.code.startAgain')}</InlineLinkButton>;
  if (showSupportContact)
    return (
      <Trans
        components={{
          linkTag: (
            <a className="text-blue-500 underline" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
          ),
        }}
        i18nKey={'page.mfaSetup.code.contactSupport'}
      />
    );
  return undefined;
};

const HaventReceivedCodeHelpText = ({
  onRetry,
  t,
  ...props
}: {
  readonly onChangeNumber?: () => void;
  readonly onStartAgain?: () => void;
  readonly onRetry: () => void | Promise<void>;
  readonly showSupportContact: boolean;
  readonly t: (key: string) => string;
}) => {
  const retryLink = <InlineLinkButton onClick={onRetry}>{t('page.mfaSetup.code.tryAgain')}</InlineLinkButton>;
  const secondLink = getSecondLink({ ...props, t });

  const helpKey = secondLink ? 'page.mfaSetup.code.helpPrefixWithSecondLink' : 'page.mfaSetup.code.helpPrefix';

  return (
    <Text>
      <Trans components={{ retryLink, ...(secondLink ? { secondLink } : undefined) }} i18nKey={helpKey} />
    </Text>
  );
};

type CodeCaptureFormProps = BaseCaptureFormProps & {
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  readonly onChangeNumber?: () => void;
  readonly onRetry?: () => void | Promise<void>;
  readonly onStartAgain?: () => void;
  readonly showSupportContact?: boolean;
};

const CodeCaptureForm = ({
  autoComplete,
  buttonLabel,
  description,
  errorRequired,
  inputLabel,
  inputMode,
  onChangeNumber,
  onRetry,
  onStartAgain,
  onSubmit,
  placeholder,
  title,
  showSupportContact = false,
}: CodeCaptureFormProps) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ value: string }>({ defaultValues: { value: '' } });

  const submitHandler = handleSubmit(
    createSubmitHandler({
      onSubmit: async ({ value }) => onSubmit(value.trim()),
      setError,
    })
  );

  const errorMessages = useCollectedErrors(errors);
  const id = useId();

  return (
    <FormLayout
      buttonLabel={buttonLabel}
      description={description}
      errorMessages={errorMessages}
      isSubmitting={isSubmitting}
      submitHandler={submitHandler}
      title={title}
    >
      {inputLabel ? <Label htmlFor={id}>{inputLabel}</Label> : null}
      <Input
        {...register('value', { required: errorRequired })}
        autoComplete={autoComplete}
        id={id}
        inputMode={inputMode}
        placeholder={placeholder}
        variant={errorMessages.length > 0 ? 'error' : 'default'}
      />
      {onRetry ? (
        <HaventReceivedCodeHelpText
          onChangeNumber={onChangeNumber}
          onRetry={onRetry}
          onStartAgain={onStartAgain}
          showSupportContact={showSupportContact}
          t={t}
        />
      ) : null}
    </FormLayout>
  );
};

export { CodeCaptureForm, HaventReceivedCodeHelpText };
